import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useRef, useEffect } from 'react';
import { __DEV__, isNullOrUndefined } from '../../utils/assertion.esm.js';
import '../../utils/ssr.esm.js';
import '../../utils/number.esm.js';
import '../../utils/array.esm.js';
import '../../utils/breakpoint.esm.js';
import '../../utils/context.esm.js';
import '../../utils/dom.esm.js';
import '../../utils/tabbable.esm.js';
import '../../utils/dom-query.esm.js';
import '../../utils/logger.esm.js';
import '../../utils/focus.esm.js';
import '../../utils/function.esm.js';
import '../../utils/keydown.esm.js';
import '../../utils/lazy.esm.js';
import '../../utils/object.esm.js';
import { mergeRefs } from '../../utils/react-helpers.esm.js';
import '../../utils/style-props.esm.js';
import '../../utils/tailwind.esm.js';
import Box from '../Box/index.esm.js';
import Flex from '../Flex/index.esm.js';
import { useId } from '@reach/auto-id';
import Label from '../Label/index.esm.js';
import Tag from './components/Tag/index.esm.js';
import useTagInputStyle from './styles.esm.js';

var TagInput = /*#__PURE__*/React__default.forwardRef(function (props, ref) {
  var tags = props.tags,
      _props$readOnly = props.readOnly,
      readOnly = _props$readOnly === void 0 ? false : _props$readOnly,
      editable = props.editable,
      maxTags = props.maxTags,
      _props$removeOnBacksp = props.removeOnBackspace,
      removeOnBackspace = _props$removeOnBacksp === void 0 ? true : _props$removeOnBacksp,
      allowLineBreak = props.allowLineBreak,
      validator = props.validator,
      onChange = props.onChange,
      _props$id = props.id,
      id = _props$id === void 0 ? "text-input-" + useId() : _props$id,
      _props$size = props.size,
      size = _props$size === void 0 ? 'md' : _props$size,
      label = props.label,
      _props$placeholder = props.placeholder,
      placeholder = _props$placeholder === void 0 ? label : _props$placeholder,
      invalid = props.invalid,
      enterKeyHint = props.enterKeyHint,
      styleProps = props.styleProps,
      onEnterPress = props.onEnterPress,
      tagRender = props.tagRender,
      _props$hasInputWrappe = props.hasInputWrapper,
      hasInputWrapper = _props$hasInputWrappe === void 0 ? true : _props$hasInputWrappe,
      onKeyDown = props.onKeyDown,
      splitChar = props.splitChar,
      _onBlur = props.onBlur,
      _props$addTagsOnBlur = props.addTagsOnBlur,
      addTagsOnBlur = _props$addTagsOnBlur === void 0 ? false : _props$addTagsOnBlur,
      onInputChangeProp = props.onInputChange,
      inputProps = _objectWithoutPropertiesLoose(props, ["tags", "readOnly", "editable", "maxTags", "removeOnBackspace", "allowLineBreak", "validator", "onChange", "id", "size", "label", "placeholder", "invalid", "enterKeyHint", "styleProps", "onEnterPress", "tagRender", "hasInputWrapper", "onKeyDown", "splitChar", "onBlur", "addTagsOnBlur", "onInputChange"]);

  var _useTagInputStyle = useTagInputStyle(props),
      wrapperStyleProps = _useTagInputStyle.wrapperStyleProps,
      inputStyleProps = _useTagInputStyle.inputStyleProps;

  var _useState = useState(''),
      input = _useState[0],
      setInput = _useState[1];

  var inputRef = React__default.createRef();
  var isEditable = readOnly ? false : editable || false;
  var maxTagsReached = maxTags !== undefined ? tags.length >= maxTags : false;
  var showInput = !readOnly && !maxTagsReached;
  var containerRef = useRef(null);

  var removeTag = function removeTag(index) {
    var newTags = [].concat(tags);
    newTags.splice(index, 1);
    onChange(newTags);
  };

  var updateTag = function updateTag(index, value) {
    var newTags = [].concat(tags);
    newTags[index] = value;
    onChange(newTags);
  };

  var addTag = function addTag(value) {
    var newTags = [].concat(tags);

    if (splitChar) {
      var items = value.split(splitChar).map(function (tag) {
        return tag.trim();
      }).filter(Boolean);
      newTags.push.apply(newTags, items);
    } else {
      newTags.push(value);
    }

    onChange(newTags);
    setInput('');

    if (onEnterPress) {
      onEnterPress(newTags);
    }
  };

  var handleNewTag = function handleNewTag() {
    // If input is blank, do nothing
    if (input === '') {
      return;
    } // Check if input is valid


    var valid = !isNullOrUndefined(validator) ? validator(input) : true;

    if (!valid) {
      return;
    } // Add input to tag list


    addTag(input);
    setTimeout(function () {
      var _containerRef$current;

      (_containerRef$current = containerRef.current) == null ? void 0 : _containerRef$current.scroll({
        left: containerRef.current.scrollWidth
      });
    });
  };

  var onInputChange = function onInputChange(e) {
    setInput(e.target.value);

    if (onInputChangeProp) {
      onInputChangeProp(e.target.value);
    }
  };

  var onInputKeyDown = function onInputKeyDown(e) {
    if (onKeyDown) {
      onKeyDown(e);
    }

    if (e.key === 'Enter') {
      // On enter
      // Prevent form submission if tag input is nested in <form>
      e.preventDefault();
      handleNewTag();
    } else if (removeOnBackspace && (e.key === 'Backspace' || e.key === 'Delete')) {
      // On backspace or delete
      // If currently typing, do nothing
      if (input !== '') {
        return;
      } // If input is blank, remove previous tag


      removeTag(tags.length - 1);
    }
  };

  useEffect(function () {
    if (showInput) {
      var _inputRef$current;

      (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus();
    } else {
      setInput('');
    }
  }, [showInput]);
  return React__default.createElement(React__default.Fragment, null, label && React__default.createElement(Label, {
    visuallyHidden: true,
    htmlFor: id
  }, label), React__default.createElement(Flex, _extends({
    dangerouslySetClassName: hasInputWrapper ? 'form-input' : undefined
  }, wrapperStyleProps, styleProps, {
    ref: mergeRefs(ref, containerRef)
  }), tags.map(function (tag, index) {
    return tagRender ? // eslint-disable-next-line react/no-array-index-key
    React__default.createElement(React__default.Fragment, {
      key: tag + index
    }, tagRender({
      tag: tag,
      index: index,
      onDelete: function onDelete() {
        return removeTag(index);
      },
      onUpdate: function onUpdate(value) {
        return updateTag(index, value);
      }
    })) : React__default.createElement(Tag // eslint-disable-next-line react/no-array-index-key
    , {
      // eslint-disable-next-line react/no-array-index-key
      key: tag + index,
      value: tag,
      index: index,
      size: size,
      editable: isEditable,
      readOnly: readOnly,
      inputRef: inputRef,
      update: updateTag,
      remove: removeTag,
      validator: validator,
      removeOnBackspace: removeOnBackspace,
      allowLineBreak: allowLineBreak
    });
  }), showInput && React__default.createElement(Box, _extends({
    as: "input",
    ref: inputRef,
    value: input,
    id: id,
    dir: "auto",
    placeholder: placeholder,
    // @ts-ignore TypeScript is a little behind the times 😉
    enterKeyHint: enterKeyHint,
    "aria-invalid": invalid,
    dangerouslySetClassName: "form-input",
    onChange: onInputChange,
    onKeyDown: onInputKeyDown,
    onBlur: function onBlur(e) {
      if (_onBlur) {
        _onBlur(e);
      }

      if (addTagsOnBlur) {
        handleNewTag();
      }
    }
  }, inputStyleProps, inputProps))));
});

if (__DEV__) {
  TagInput.displayName = 'TagInput';
}

export default TagInput;
//# sourceMappingURL=index.esm.js.map
